import {
  TwilioSendVerificationCodeParams,
  TwilioVerifyCodeParams,
} from 'src/features/verification/data/service/TwilioVerificationService.types';
import {
  SendVerificationCodeResponse,
  VerifyCodeResponse,
} from 'src/features/verification/data/service/VerificationService.types';

export interface TwilioVerificationRepositoryInterface {
  sendVerificationCode(
    params: TwilioSendVerificationCodeParams
  ): Promise<SendVerificationCodeResponse | void>;
  verifyCode(
    params: TwilioVerifyCodeParams
  ): Promise<VerifyCodeResponse | void>;
}

export enum TwilioVerificationErrorCode {
  InvalidCode = '2404',
  TooManyAttempts = '60203',
  UnknownError = 'unknown_error',
  Unauthorized = '401',
  InvalidPhoneNumber = '60200',
  ChannelDisabled = '60223',
  AlreadyExists = '400',
}

export const verificationErrorMessages: Record<
  TwilioVerificationErrorCode,
  string
> = {
  [TwilioVerificationErrorCode.InvalidCode]:
    'El código de verificación es inválido.',
  [TwilioVerificationErrorCode.InvalidPhoneNumber]:
    'El número de teléfono es inválido.',
  [TwilioVerificationErrorCode.TooManyAttempts]:
    'Demasiados intentos fallidos.',
  [TwilioVerificationErrorCode.UnknownError]:
    'Ocurrió un error desconocido. Por favor, intenta nuevamente.',
  [TwilioVerificationErrorCode.Unauthorized]:
    'No autorizado. Por favor, inicia sesión.',
  [TwilioVerificationErrorCode.ChannelDisabled]:
    'El canal seleccionado está deshabilitado. Por favor, intenta enviar el código utilizando {{alternative_channel}}',
  [TwilioVerificationErrorCode.AlreadyExists]:
    'Ya existe un usuario con este número de teléfono o correo electrónico.',
};

export class TwilioVerificationError extends Error {
  code: TwilioVerificationErrorCode;

  constructor(
    code: TwilioVerificationErrorCode | TwilioVerificationErrorCode[]
  ) {
    const resolvedCode = TwilioVerificationError.resolveCode(code);
    const message =
      verificationErrorMessages[resolvedCode] ||
      verificationErrorMessages[TwilioVerificationErrorCode.UnknownError];

    super(message);

    this.name = 'TwilioVerificationError';
    this.code = resolvedCode;

    Object.setPrototypeOf(this, new.target.prototype);
  }

  static resolveCode<T extends TwilioVerificationErrorCode>(
    code: T | T[]
  ): TwilioVerificationErrorCode {
    if (Array.isArray(code)) {
      return (
        code.find((c) =>
          Object.values(TwilioVerificationErrorCode).includes(c)
        ) || TwilioVerificationErrorCode.UnknownError
      );
    }

    if (Object.values(TwilioVerificationErrorCode).includes(code)) {
      return code;
    }

    return TwilioVerificationErrorCode.UnknownError;
  }
}
